import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Container,
  Breadcrumbs,
  Fade,
  Divider,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";

// アイコン
import SettingsIcon from "@mui/icons-material/Settings";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';

import Header from "../../../components/Header";
import { useNavigate } from "react-router-dom";

const SettingsSharedRequests = () => {
  const [requests, setRequests] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedView, setSharedView] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSharedStatus = async () => {
      try {
        const res = await fetch("/api/shared/view", {
          headers: {
            Authorization: "Bearer " + sessionStorage.getItem("token"),
          },
        });
        const data = await res.json();
        if (data.sharedView) {
          alert("共有中アカウントではこの画面にアクセスできません。");
          window.location.href = "/";
        } else {
          setSharedView(false);
        }
      } catch (err) {

        alert("エラーが発生しました");
        window.location.href = "/";
      }
    };
    checkSharedStatus();
  }, []);

  useEffect(() => {
    if (sharedView === false) {
      const fetchAll = async () => {
        try {
          const token = sessionStorage.getItem("token");

          const [pendingRes, sharedUsersRes] = await Promise.all([
            axios.get("/api/shared/requests", {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get("/api/shared/all-shared-users", {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

          setRequests(pendingRes.data);
          setSharedUsers(sharedUsersRes.data);
        } catch (err) {
          setError("申請情報の取得に失敗しました。");
        } finally {
          setLoading(false);
        }
      };

      fetchAll();
    }
  }, [sharedView]);

  const handleApprove = async (requestId) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.post(`/api/shared/requests/${requestId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      const message = err?.response?.data || "承認に失敗しました。";
      alert(message);
    }
  };

  const handleReject = async (requestId) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.post(`/api/shared/requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      alert("拒否に失敗しました。");
    }
  };

  const openConfirmDialog = (userId) => {
    setSelectedUserId(userId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedUserId(null);
  };

  const handleRevoke = async () => {
    if (!selectedUserId) return;
    
    try {
      const token = sessionStorage.getItem("token");
      await axios.post(`/api/shared/remove/${selectedUserId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSharedUsers((prev) => prev.filter((r) => r.sharedUserId !== selectedUserId));
      closeConfirmDialog();
    } catch (err) {
      alert("共有の解除に失敗しました。");
      closeConfirmDialog();
    }
  };

  if (sharedView === null) {
    return (
      <>
        <Header />
        <Container 
          maxWidth="md" 
          sx={{ 
            mt: 4, 
            display: 'flex', 
            justifyContent: 'center',
            // PWAボトムナビゲーション対応
            pb: { xs: 12, sm: 6 },
            minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
          }}
        >
          <CircularProgress color="primary" />
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4, 
          // PWAボトムナビゲーション対応：安全マージンを確保
          mb: { xs: 2, sm: 6 }, 
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          px: { xs: 2, sm: 3 },
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
        }}
      >
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/setting-Home')}
          >
            <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            設定
          </Typography>
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/settings/shared')}
          >
            <PeopleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            共有設定
          </Typography>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <CheckCircleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            共有申請一覧
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                共有申請と管理
              </Typography>
              <Typography color="text.secondary">
                共有申請の承認や現在共有しているユーザーの管理ができます
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            ) : (
              <Stack spacing={4}>
                {/* 承認待ちの申請 */}
                <Card 
                  elevation={2} 
                  sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{ 
                      px: 3, 
                      py: 2, 
                      bgcolor: 'warning.light',
                      color: 'warning.dark',
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsIcon />
                      <Typography variant="subtitle1" fontWeight="medium">
                        承認待ちの共有申請
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${requests.length}件`} 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    {requests.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <InfoIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                        <Typography color="text.secondary">
                          現在承認待ちの申請はありません
                        </Typography>
                      </Box>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {requests.map((req) => (
                          <ListItem
                            key={req.id}
                            divider
                            sx={{ 
                              p: 0, 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'stretch', sm: 'center' },
                              '&:last-child': { borderBottom: 'none' }
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                p: 2,
                                width: { xs: '100%', sm: 'auto' },
                                flex: 1
                              }}
                            >
                              <Avatar 
                                sx={{ 
                                  bgcolor: 'warning.light',
                                  color: 'warning.dark',
                                  mr: 2 
                                }}
                              >
                                {req.displayName?.charAt(0) || req.username?.charAt(0) || 'U'}
                              </Avatar>
                              <ListItemText
                                primary={
                                  <Typography fontWeight="medium">
                                    {req.displayName || req.username}
                                  </Typography>
                                }
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="body2" component="span">
                                      コード: 
                                    </Typography>
                                    <Chip 
                                      label={req.code} 
                                      size="small" 
                                      color="primary" 
                                      sx={{ fontWeight: 'medium', letterSpacing: 0.5 }}
                                    />
                                  </Box>
                                }
                              />
                            </Box>
                            <Box 
                              sx={{ 
                                p: { xs: 2, sm: 1 }, 
                                pt: { xs: 0, sm: 1 },
                                borderTop: { xs: '1px solid', sm: 'none' },
                                borderColor: 'divider',
                                display: 'flex',
                                gap: 1,
                                justifyContent: { xs: 'space-between', sm: 'flex-end' }
                              }}
                            >
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckIcon />}
                                onClick={() => handleApprove(req.id)}
                                sx={{ flex: { xs: 1, sm: 'none' } }}
                              >
                                承認
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={() => handleReject(req.id)}
                                sx={{ flex: { xs: 1, sm: 'none' } }}
                              >
                                拒否
                              </Button>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>

                {/* 共有中のユーザー */}
                <Card 
                  elevation={2} 
                  sx={{ 
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{ 
                      px: 3, 
                      py: 2, 
                      bgcolor: 'primary.light',
                      color: 'primary.dark',
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon />
                      <Typography variant="subtitle1" fontWeight="medium">
                        現在共有中のユーザー
                      </Typography>
                    </Box>
                    <Chip 
                      label={`${sharedUsers.length}件`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    {sharedUsers.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <InfoIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                        <Typography color="text.secondary">
                          現在共有中のユーザーはいません
                        </Typography>
                      </Box>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {sharedUsers.map((user) => (
                          <ListItem
                            key={user.sharedUserId}
                            divider
                            sx={{ 
                              p: 0, 
                              display: 'flex', 
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'stretch', sm: 'center' },
                              '&:last-child': { borderBottom: 'none' }
                            }}
                          >
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                p: 2,
                                width: { xs: '100%', sm: 'auto' },
                                flex: 1
                              }}
                            >
                              <Avatar 
                                sx={{ 
                                  bgcolor: user.active ? 'success.light' : 'grey.200',
                                  color: user.active ? 'success.dark' : 'text.secondary',
                                  mr: 2 
                                }}
                              >
                                {user.username?.charAt(0) || 'U'}
                              </Avatar>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography fontWeight="medium">
                                      {user.username}
                                    </Typography>
                                    {user.active && (
                                      <Chip 
                                        label="参加中" 
                                        size="small" 
                                        color="success" 
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={user.active ? "共有アクティブ" : "非アクティブ"}
                              />
                            </Box>
                            <Box 
                              sx={{ 
                                p: { xs: 2, sm: 1 }, 
                                pt: { xs: 0, sm: 1 },
                                borderTop: { xs: '1px solid', sm: 'none' },
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                                width: { xs: '100%', sm: 'auto' }
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => openConfirmDialog(user.sharedUserId)}
                                fullWidth={!!(window.innerWidth < 600)}
                              >
                                共有を解除
                              </Button>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            )}

            {/* 戻るボタン */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/settings/shared")}
                size="large"
                sx={{ minWidth: 200 }}
              >
                共有設定に戻る
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          共有解除の確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            このユーザーとの共有を解除すると、ユーザーはあなたのデータにアクセスできなくなります。この操作は元に戻せません。
            本当に共有を解除しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleRevoke} color="error" variant="contained" autoFocus>
            共有を解除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsSharedRequests;